import React, { useContext, useEffect, useRef, useState } from 'react';
import _ from 'lodash';

import MetricPreview from './MetricPreview';
import GqlClientContext from '../../../../contexts/GqlClientContext';
import MetricCacheContext from '../../../../contexts/MetricCacheContext';
import useDateScope from '../../../../hooks/useDateScope';
import useNetworkingEffect from '../../../../hooks/useNetworkingEffect';
import aggregateMetric from '../../../../api/aggregateMetric';
import aggregateCompoundMetric from '../../../../api/aggregateCompoundMetric';
import metricTypeCheckers from '../../../../types/metricTypeCheckers';
import MetricOptionsContext from '../../../../contexts/MetricOptionsContext';
import isDefined from '../../../../isDefined';
import DateInputContext from '../../../../contexts/DateInputContext';
import CompoundMetricsContext from '../../../../contexts/CompoundMetricsContext';
import useValueFormatters from '../../../../hooks/useValueFormatters';
import useToMetricInput from 'hooks/useToMetricInput';
import { DateTime } from 'luxon';
import isValidCompoundMetricExpression from '../isValidCompoundMetricExpression';
import captureException from 'services/captureException';

const BLANK_FILTERS = [{}];

const useMetricPreview = (metric?: Metrics.NormalMetric) => {
  const { client } = useContext(GqlClientContext);
  const [data, setData] = useState<string>('');
  const dateScope = useDateScope({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { formatMetric } = useValueFormatters();
  const toMetricInput = useToMetricInput();

  useNetworkingEffect(() => {
    if (!metric) {
      return;
    }
    let isActive = true;
    const filterInput = {
      keywords: [],
      ranges: [],
      booleanFilters: [],
      wildcardFilters: [],
    } as FilterInput;
    setIsLoading(true);
    aggregateMetric(
      toMetricInput(metric),
      [filterInput],
      dateScope,
      client,
    ).then((d) => {
      if (!isActive) {
        return;
      }
      const v = d[0][metric.id] as number | string | string[];
      setData(formatMetric({ value: v, draftMetric: metric }));
      setIsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [dateScope, client, metric, formatMetric]);

  return {
    data,
    isLoading,
  };
};

const useCompoundMetricPreview = (compoundMetric?: Metrics.CompoundMetric) => {
  const { client } = useContext(GqlClientContext);
  const { requestMetricsByIds, isIdErrored } = useContext(MetricCacheContext);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);

  // Ensure the compound metric's input metrics (which may be single-use and
  // excluded from requestAllNormalMetrics) are fetched by ID into the cache.
  useEffect(() => {
    if (compoundMetric) {
      requestMetricsByIds(compoundMetric.metricIds);
    }
  }, [compoundMetric, requestMetricsByIds]);

  // Report genuinely-missing input metrics as their own error. isIdErrored is
  // only true once an ID has been queried and found in neither the normal nor
  // compound collections (i.e. deleted/missing) — distinct from the transient
  // "still loading" state — so this won't fire for metrics that are merely
  // mid-fetch. Logged once per compound metric + missing-id set so the effect
  // re-running doesn't spam Sentry.
  const loggedMissingInputsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!compoundMetric) {
      return;
    }
    const missingInputIds = compoundMetric.metricIds.filter((id) =>
      isIdErrored(id),
    );
    if (missingInputIds.length === 0) {
      return;
    }
    const key = `${compoundMetric.id}:${[...missingInputIds].sort().join(',')}`;
    if (loggedMissingInputsRef.current.has(key)) {
      return;
    }
    loggedMissingInputsRef.current.add(key);
    const error = new Error(
      `Compound metric "${compoundMetric.id}" references input metric(s) not found: ${missingInputIds.join(', ')}`,
    );
    error.name = 'CompoundMetricPreview - input metric not found';
    captureException(error);
  }, [compoundMetric, isIdErrored]);

  const dateScope = useDateScope({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<string>('');
  const [def, setDef] = useState<
    Metrics.CompoundMetricWithMetricDefs | undefined
  >();
  const { formatMetric } = useValueFormatters();
  const toMetricInput = useToMetricInput();

  useEffect(() => {
    if (!compoundMetric) {
      return undefined;
    }

    const metrics = compoundMetric.metricIds
      .map((mid) => metricOptionsLookup[mid])
      .filter(isDefined)
      .filter(metricTypeCheckers.isNormalMetric);
    setDef({
      id: compoundMetric.id,
      name: compoundMetric.name,
      metrics,
      expression: compoundMetric.expression,
    });
  }, [compoundMetric, metricOptionsLookup]);

  useNetworkingEffect(() => {
    if (!def || !compoundMetric) {
      return;
    }

    // Only send the request once the expression evaluates locally against the
    // metrics actually included in the payload. While the metric cache is still
    // loading, def.metrics can be a partial subset while the expression still
    // references unresolved IDs — sending that yields a backend "Invalid
    // expression" error. Skipping here avoids the noise; the effect re-runs when
    // def updates (cache fills) and the request fires then.
    if (
      !isValidCompoundMetricExpression({
        expression: def.expression,
        metricIds: def.metrics.map((m) => m.id),
      })
    ) {
      setIsLoading(false);
      return;
    }

    if (
      (dateScope.startDate && !DateTime.fromISO(dateScope.startDate).isValid) ||
      (dateScope.endDate && !DateTime.fromISO(dateScope.endDate).isValid)
    ) {
      return;
    }

    setIsLoading(true);
    let isActive = true;

    aggregateCompoundMetric({
      compoundMetric: def,
      filterInput: BLANK_FILTERS,
      dateScope,
      toMetricInput,
      client,
    }).then((d) => {
      if (!isActive) {
        return;
      }

      const v = d[0][compoundMetric.id];
      const formattedV = formatMetric({
        value: v,
        draftMetric: compoundMetric,
      });

      setData(formattedV);
      setIsLoading(false);
    });

    return () => {
      isActive = false;
    };
  }, [client, compoundMetric, dateScope, def, formatMetric]);

  return {
    data,
    isLoading,
  };
};

const useData = (
  metric: Metrics.NormalMetric | Metrics.CompoundMetric,
  isValid: boolean,
) => {
  const metricPreview = useMetricPreview(
    isValid && metricTypeCheckers.isNormalMetric(metric) ? metric : undefined,
  );
  const compoundMetricPreview = useCompoundMetricPreview(
    isValid && metricTypeCheckers.isCompoundMetric(metric) ? metric : undefined,
  );

  if (metricTypeCheckers.isNormalMetric(metric)) {
    return metricPreview;
  } else {
    return compoundMetricPreview;
  }
};

const useSetDataTypes = (
  metric: Metrics.NormalMetric | Metrics.CompoundMetric,
) => {
  const { compoundMetricInputsLookup } = useContext(CompoundMetricsContext);
  const { setDataTypes } = useContext(DateInputContext);
  useEffect(() => {
    if (metricTypeCheckers.isNormalMetric(metric)) {
      setDataTypes([metric.dataType]);
    } else {
      const metrics = compoundMetricInputsLookup[metric.id];
      if (!metrics) {
        setDataTypes([]);
        return;
      }

      setDataTypes(_.uniq(metrics.map((m) => m.dataType)));
    }
  }, [compoundMetricInputsLookup, metric, setDataTypes]);
};

const MetricPreviewContainer = ({
  metric,
  isValid,
}: {
  metric: Metrics.NormalMetric | Metrics.CompoundMetric;
  isValid: boolean;
}) => {
  const [invalidMessage, setInvalidMessage] = useState<string | undefined>(
    undefined,
  );
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const { data, isLoading } = useData(metric, isValid);
  useSetDataTypes(metric);

  const dateScope = useDateScope({});

  useEffect(() => {
    if (!isValid) {
      setInvalidMessage('Invalid Metric');
      setIsLoadingPreview(false);
    } else if (
      (dateScope.startDate && !DateTime.fromISO(dateScope.startDate).isValid) ||
      (dateScope.endDate && !DateTime.fromISO(dateScope.endDate).isValid)
    ) {
      setInvalidMessage('Invalid Date Range');
      setIsLoadingPreview(false);
    } else {
      setInvalidMessage(undefined);
      setIsLoadingPreview(isLoading);
    }
  }, [dateScope, isLoading, isValid]);

  return (
    <MetricPreview
      data={invalidMessage || data}
      invalidMessage={invalidMessage}
      isLoading={isLoadingPreview}
    />
  );
};

export default MetricPreviewContainer;
